import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { QrCode, CheckCircle, TrendingUp, Users, FileDown } from 'lucide-react';
import { exportToExcel } from '../services/exportService';

const Presences = () => {
  const [presences, setPresences] = useState([]);
  const [etudiants, setEtudiants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanCode, setScanCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [qrCode, setQrCode] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user?.role === 'ADMIN';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const etudiantsRes = await axios.get('/api/etudiants?limit=200', { headers, timeout: 8000 });
      setEtudiants(etudiantsRes.data?.data || []);

      const presencesRes = await axios.get('/api/presences', { headers, timeout: 8000 });
      setPresences(presencesRes.data || []);

      setError('');
    } catch (err) {
      console.error('Erreur:', err);
      setError('Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  };

  const getTauxPresence = (etudiantId) => {
    const presEtudiant = presences.filter(p => p.etudiant_id === etudiantId);
    const total = presEtudiant.length;
    const presents = presEtudiant.filter(p => p.present).length;
    return total > 0 ? Math.round((presents / total) * 100) : 0;
  };

  const getStatutPresence = (taux) => {
    if (taux >= 75) return { text: 'Bon', color: '#166534', bg: '#dcfce7' };
    if (taux >= 50) return { text: 'Moyen', color: '#92400e', bg: '#fef3c7' };
    return { text: 'Critique', color: '#991b1b', bg: '#fee2e2' };
  };

  const filteredEtudiants = etudiants.filter(e => {
    const matchesSearch = searchTerm === '' || 
      `${e.Nom} ${e.Prénom}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (e.CNE || '').includes(searchTerm);
    return matchesSearch;
  });

  const handleScanPresence = async () => {
    if (!scanCode) {
      setMessage('Veuillez entrer un ID étudiant');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    setScanning(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/presences/scan', {
        etudiant_id: scanCode
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000
      });

      setMessage('Présence enregistrée avec succès !');
      setShowScanModal(false);
      setScanCode('');
      loadData();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Erreur lors du scan');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setScanning(false);
    }
  };

  const generateQRCode = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post('/api/presences/generate-qr', {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000
      });
      setQrCode(res.data.qrCode);
      setTimeout(() => setQrCode(''), 10000);
    } catch (err) {
      console.error('Erreur génération QR:', err);
    }
  };

  const handleExport = () => {
    const columns = [
      { key: 'cne', label: 'CNE' },
      { key: 'etudiant', label: 'Étudiant' },
      { key: 'taux', label: 'Taux présence (%)' },
      { key: 'statut', label: 'Statut' },
      { key: 'scans', label: 'Nombre de scans' }
    ];
    const data = filteredEtudiants.map(e => {
      const id = e._id;
      const taux = getTauxPresence(id);
      const statut = getStatutPresence(taux);
      return {
        cne: e.CNE || '-',
        etudiant: `${e.Nom} ${e.Prénom}`,
        taux,
        statut: statut.text,
        scans: presences.filter(p => p.etudiant_id === id).length
      };
    });
    exportToExcel(data, 'Presences', columns);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div className="loading-spinner"></div>
        <p>Chargement des présences...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p style={{ color: '#ef4444' }}>{error}</p>
        <button onClick={loadData} style={{ padding: '10px 20px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 25%, #f3e8ff 100%)',
      padding: '1.5rem'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <h1 style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937' }}>Gestion des Présences</h1>
            <p style={{ color: '#6b7280' }}>
              {etudiants.length} étudiant{etudiants.length > 1 ? 's' : ''} inscrit{etudiants.length > 1 ? 's' : ''}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {isAdmin && (
              <>
                <button
                  onClick={() => setShowScanModal(true)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#10b981',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <QrCode size={18} />
                  Scanner QR Code
                </button>
                <button
                  onClick={generateQRCode}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#8b5cf6',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  Générer QR Code
                </button>
              </>
            )}
            <button
              onClick={handleExport}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#059669',
                border: 'none',
                borderRadius: '12px',
                color: 'white',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <FileDown size={18} />
              Export Excel
            </button>
          </div>
        </div>

        {message && (
          <div style={{
            background: message.includes('succès') ? '#dcfce7' : '#fee2e2',
            color: message.includes('succès') ? '#166534' : '#991b1b',
            padding: '1rem',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}>
            {message}
          </div>
        )}

        {qrCode && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '1rem',
            marginBottom: '1.5rem',
            textAlign: 'center',
            border: '2px solid #8b5cf6'
          }}>
            <p style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Code QR</p>
            <p style={{ fontFamily: 'monospace', fontSize: '18px', letterSpacing: '2px' }}>{qrCode}</p>
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '0.5rem' }}>Valable 10 minutes</p>
          </div>
        )}

        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Recherche</label>
            <input
              type="text"
              placeholder="Nom, prénom, CNE..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%', padding: '0.625rem', border: '1px solid #e5e7eb', borderRadius: '8px' }}
            />
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ background: 'white', padding: '1rem', borderRadius: '12px', textAlign: 'center' }}>
            <Users size={24} style={{ margin: '0 auto 0.5rem', color: '#3b82f6' }} />
            <p style={{ fontSize: '24px', fontWeight: 'bold' }}>{filteredEtudiants.length}</p>
            <p style={{ color: '#6b7280' }}>Étudiants</p>
          </div>
          <div style={{ background: 'white', padding: '1rem', borderRadius: '12px', textAlign: 'center' }}>
            <CheckCircle size={24} style={{ margin: '0 auto 0.5rem', color: '#10b981' }} />
            <p style={{ fontSize: '24px', fontWeight: 'bold' }}>{presences.length}</p>
            <p style={{ color: '#6b7280' }}>Présences</p>
          </div>
          <div style={{ background: 'white', padding: '1rem', borderRadius: '12px', textAlign: 'center' }}>
            <TrendingUp size={24} style={{ margin: '0 auto 0.5rem', color: '#8b5cf6' }} />
            <p style={{ fontSize: '24px', fontWeight: 'bold' }}>
              {filteredEtudiants.length > 0 
                ? Math.round(presences.filter(p => filteredEtudiants.some(e => e._id === p.etudiant_id)).length / filteredEtudiants.length * 100) 
                : 0}%
            </p>
            <p style={{ color: '#6b7280' }}>Taux moyen</p>
          </div>
        </div>

        <div style={{
          background: 'white',
          borderRadius: '20px',
          overflowX: 'auto',
          boxShadow: '0 4px 6px rgba(0,0,0,0.07)'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '1rem', textAlign: 'left' }}>CNE</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Étudiant</th>
                <th style={{ padding: '1rem', textAlign: 'center' }}>Taux présence</th>
                <th style={{ padding: '1rem', textAlign: 'center' }}>Statut</th>
                <th style={{ padding: '1rem', textAlign: 'center' }}>Scans</th>
              </tr>
            </thead>
            <tbody>
              {filteredEtudiants.map((etudiant) => {
                const id = etudiant._id;
                const taux = getTauxPresence(id);
                const statut = getStatutPresence(taux);
                const scans = presences.filter(p => p.etudiant_id === id).length;

                return (
                  <tr key={id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '1rem', fontWeight: '600', color: '#3b82f6' }}>{etudiant.CNE || '-'}</td>
                    <td style={{ padding: '1rem' }}>
                      <div>
                        <div style={{ fontWeight: '500' }}>{etudiant.Nom} {etudiant.Prénom}</div>
                        <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>ID: {id}</div>
                      </div>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold', color: taux >= 75 ? '#10b981' : taux >= 50 ? '#f59e0b' : '#ef4444' }}>
                      {taux}%
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '20px',
                        background: statut.bg,
                        color: statut.color
                      }}>
                        {statut.text}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <button
                        onClick={() => {
                          setScanCode(id);
                          setShowScanModal(true);
                        }}
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#3b82f6',
                          border: 'none',
                          borderRadius: '8px',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Scanner
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filteredEtudiants.length === 0 && (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
                    Aucun étudiant trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {showScanModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '20px',
              width: '90%',
              maxWidth: '450px',
              padding: '25px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h2>Scanner un QR Code</h2>
                <button onClick={() => setShowScanModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>✕</button>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label>ID Étudiant</label>
                <input
                  type="text"
                  value={scanCode}
                  onChange={(e) => setScanCode(e.target.value)}
                  placeholder="ID de l'étudiant"
                  style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '8px', marginTop: '5px' }}
                />
              </div>

              <button
                onClick={handleScanPresence}
                disabled={scanning}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: scanning ? '#9ca3af' : '#10b981',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontWeight: '600',
                  cursor: scanning ? 'not-allowed' : 'pointer'
                }}
              >
                {scanning ? 'Traitement...' : 'Valider la présence'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Presences;


